import type { BoundaryStyle } from "../types/boundaryStyle";
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../utils/api'
import { ApiResponse, ApiError } from '../types/api'
import { Project, loadingLayers } from '../types/project'

// Pinned projects lead the listing, most-recently-pinned first; the rest are
// oldest-first. The API exposes no created timestamp, but ids are
// auto-incrementing, so ascending id == oldest first. The backend already
// returns this order — re-sorting here keeps it stable through the optimistic
// cache writes in usePinProject, which don't go via the server.
const sortPinnedFirst = (response: ApiResponse<Project[]>): ApiResponse<Project[]> => ({
    ...response,
    data: [...(response.data ?? [])].sort((a, b) => {
        if (!!a.is_pinned !== !!b.is_pinned) return a.is_pinned ? -1 : 1;
        if (a.is_pinned && b.is_pinned) {
            // A just-pinned project has no pinned_at until the refetch lands;
            // treat it as the newest pin so it doesn't flicker down the list.
            const at = a.pinned_at ? Date.parse(a.pinned_at) : Infinity;
            const bt = b.pinned_at ? Date.parse(b.pinned_at) : Infinity;
            if (at !== bt) return bt - at;
        }
        return Number(a.id) - Number(b.id);
    }),
});

export const useProjects = (
    searchQuery?: string,
    tagFilter?: string,
    options: { enabled?: boolean } = {}
) => {
    const queryParams = new URLSearchParams();
    if (searchQuery) {
        queryParams.append('search', searchQuery);
    }
    if (tagFilter) {
        queryParams.append('tags', tagFilter);
    }
    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    
    const { data: projects, isLoading, error } = useQuery({
        // Normalised so useProjects() and useProjects('', '') share one entry.
        queryKey: ['projects', searchQuery || '', tagFilter || ''],
        queryFn:  () => api.request<ApiResponse<Project[]>>(`/api/projects${queryString}`),
        select: sortPinnedFirst,
        enabled: options.enabled ?? true,
        refetchOnWindowFocus: false,
        // Poll fast while any project is processing AND has a task_id; more
        // slowly while any open project still has layers loading behind it
        // (the chip on its row); stop otherwise.
        refetchInterval: (query) => {
            try {
                const list = (query as any)?.state?.data?.data as Project[] | undefined;
                const anyProcessing = (list || []).some((p) => (p.status === 'processing' || p.status === 'expanding' || p.status === 'refreshing') && !!p.task_id);
                if (anyProcessing) return 3000;
                const anyLoading = (list || []).some((p) => loadingLayers(p.layer_status).length > 0);
                return anyLoading ? 5000 : false;
            } catch {
                return false;
            }
        },
    }) as { data: ApiResponse<Project[]>, isLoading: boolean, error: ApiError };

    return { projects, isLoading, error };
};


export const useAddProject = () => {
    const queryClient = useQueryClient();

    return useMutation<ApiResponse<any>, ApiError, FormData>({ 
        mutationFn: (formData: FormData) => {
            console.log('API Request - FormData entries:');
            for (const pair of formData.entries()) {
                console.log(pair[0], pair[1]);
            }
            return api.request<ApiResponse<any>>(`/api/projects`, { 
                method: 'POST', 
                body: formData,
                // Don't set Content-Type header, let the browser set it with the boundary
                headers: {}
            }); 
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['projects']});
        },
    });
};



export const useArchiveProject = () => {
    const queryClient = useQueryClient();

    return useMutation<ApiResponse<any>, ApiError, string>({
      mutationFn: (projectId: string) => {
        return api.request<ApiResponse<any>>(`/api/projects/${projectId}`, {
          method: 'DELETE',
        });
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['projects'] });
        queryClient.invalidateQueries({ queryKey: ['projects-archived'] });
      },
      onError: (error) => {
        console.error('Error archiving project:', error);
      },
    });
};

export const useArchivedProjects = (searchQuery?: string, tagFilter?: string) => {
    const queryParams = new URLSearchParams();
    if (searchQuery) {
        queryParams.append('search', searchQuery);
    }
    if (tagFilter) {
        queryParams.append('tags', tagFilter);
    }
    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';

    const { data: projects, isLoading, error } = useQuery({
        queryKey: ['projects-archived', searchQuery, tagFilter],
        queryFn: () => api.request<ApiResponse<Project[]>>(`/api/projects/archived${queryString}`),
        select: sortPinnedFirst,
        refetchOnWindowFocus: false,
        // Poll while any project is mid-deletion so its card clears once the
        // background cleanup finishes; stop otherwise.
        refetchInterval: (query) => {
            try {
                const list = (query as any)?.state?.data?.data as Project[] | undefined;
                const anyDeleting = (list || []).some((p) => p.status === 'deleting');
                return anyDeleting ? 3000 : false;
            } catch {
                return false;
            }
        },
    }) as { data: ApiResponse<Project[]>, isLoading: boolean, error: ApiError };

    return { projects, isLoading, error };
};

export const useRestoreProject = () => {
    const queryClient = useQueryClient();

    return useMutation<ApiResponse<any>, ApiError, string>({
      mutationFn: (projectId: string) => {
        return api.request<ApiResponse<any>>(`/api/projects/${projectId}/restore`, {
          method: 'POST',
        });
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['projects'] });
        queryClient.invalidateQueries({ queryKey: ['projects-archived'] });
      },
      onError: (error) => {
        console.error('Error restoring project:', error);
      },
    });
};

export const usePermanentlyDeleteProject = () => {
    const queryClient = useQueryClient();

    return useMutation<ApiResponse<any>, ApiError, string>({
      mutationFn: (projectId: string) => {
        return api.request<ApiResponse<any>>(`/api/projects/archived/${projectId}`, {
          method: 'DELETE',
        });
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['projects-archived'] });
      },
      onError: (error) => {
        console.error('Error permanently deleting project:', error);
      },
    });
};

/**
 * Pin or unpin a project. Pins are tenant-wide: the project moves to the top
 * of the listing for everyone, not just the person who pinned it.
 *
 * The cache is updated optimistically because the whole point of the action is
 * the card changing position — waiting a round-trip for that makes the click
 * feel like it did nothing. `select` re-sorts on every read, so flipping the
 * flag in place is enough to move the card.
 */
export const usePinProject = () => {
    const queryClient = useQueryClient();

    return useMutation<
      ApiResponse<Project>,
      ApiError,
      { projectId: string; pinned: boolean },
      { previous: [readonly unknown[], ApiResponse<Project[]>][] }
    >({
      mutationFn: ({ projectId, pinned }) => {
        return api.request<ApiResponse<Project>>(`/api/projects/${projectId}/pin`, {
          method: pinned ? 'POST' : 'DELETE',
        });
      },
      onMutate: async ({ projectId, pinned }) => {
        // Stop an in-flight listing refetch from landing on top of the
        // optimistic write and snapping the card back.
        await queryClient.cancelQueries({ queryKey: ['projects'] });

        const previous = queryClient.getQueriesData<ApiResponse<Project[]>>({
          queryKey: ['projects'],
        }) as [readonly unknown[], ApiResponse<Project[]>][];

        // Every ['projects', search, tags] variant is patched, so the card
        // stays pinned if the user changes the search box mid-flight.
        previous.forEach(([key, value]) => {
          if (!value?.data) return;
          queryClient.setQueryData(key, {
            ...value,
            data: value.data.map((p) =>
              String(p.id) === String(projectId)
                // pinned_at is left null until the refetch brings the real
                // stamp; sortPinnedFirst reads that as "newest pin".
                ? { ...p, is_pinned: pinned, pinned_at: null }
                : p
            ),
          });
        });

        return { previous };
      },
      onError: (error, _variables, context) => {
        console.error('Error updating project pin:', error);
        context?.previous.forEach(([key, value]) => {
          queryClient.setQueryData(key, value);
        });
      },
      onSettled: () => {
        // Reconcile against the server so pinned_at (and therefore the order
        // within the pinned block) is the real one.
        queryClient.invalidateQueries({ queryKey: ['projects'] });
      },
    });
};

export const useAddProjectTag = () => {
    const queryClient = useQueryClient();
  
    return useMutation<ApiResponse<Project>, ApiError, { projectId: string; tagName: string }>({
      mutationFn: ({ projectId, tagName }) => {
        return api.request<ApiResponse<Project>>(`/api/projects/${projectId}/tags`, {
          method: 'POST',
          body: JSON.stringify({ tag_name: tagName }),
          headers: {
            'Content-Type': 'application/json',
          },
        });
      },
      onSuccess: (_data, variables) => {
        // Invalidate both the projects list and the individual project query
        queryClient.invalidateQueries({ queryKey: ['projects'] });
        queryClient.invalidateQueries({ queryKey: ['project', variables.projectId] });
        // Force refetch the project detail to get updated tags
        queryClient.refetchQueries({ 
          queryKey: ['project', variables.projectId],
          type: 'active'
        });
      },
    });
  };

export const useRemoveProjectTag = () => {
    const queryClient = useQueryClient();
  
    return useMutation<ApiResponse<Project>, ApiError, { projectId: string; tagName: string }>({
      mutationFn: ({ projectId, tagName }) => {
        return api.request<ApiResponse<Project>>(`/api/projects/${projectId}/tags`, {
          method: 'DELETE',
          body: JSON.stringify({ tag_name: tagName }),
          headers: {
            'Content-Type': 'application/json',
          },
        });
      },
      onSuccess: (_data, variables) => {
        // Invalidate both the projects list and the individual project query
        queryClient.invalidateQueries({ queryKey: ['projects'] });
        queryClient.invalidateQueries({ queryKey: ['project', variables.projectId] });
        // Force refetch the project detail to get updated tags
        queryClient.refetchQueries({ 
          queryKey: ['project', variables.projectId],
          type: 'active'
        });
      },
    });
  };

/**
 * File a project in a folder (null = top level). Optimistic for the same
 * reason as pinning: the row leaving one folder and appearing in another is
 * the whole feedback, and a round-trip delay reads as a dropped action.
 */
export const useMoveProjectToFolder = () => {
    const queryClient = useQueryClient();

    return useMutation<
      ApiResponse<Project>,
      ApiError,
      { projectId: string; folderId: number | null },
      { previous: [readonly unknown[], ApiResponse<Project[]>][] }
    >({
      mutationFn: ({ projectId, folderId }) => {
        return api.request<ApiResponse<Project>>(`/api/projects/${projectId}/`, {
          method: 'PATCH',
          body: { folder: folderId },
        });
      },
      onMutate: async ({ projectId, folderId }) => {
        await queryClient.cancelQueries({ queryKey: ['projects'] });
        const previous = queryClient.getQueriesData<ApiResponse<Project[]>>({
          queryKey: ['projects'],
        }) as [readonly unknown[], ApiResponse<Project[]>][];
        previous.forEach(([key, value]) => {
          if (!value?.data) return;
          queryClient.setQueryData(key, {
            ...value,
            data: value.data.map((p) =>
              String(p.id) === String(projectId) ? { ...p, folder: folderId } : p
            ),
          });
        });
        return { previous };
      },
      onError: (error, _variables, context) => {
        console.error('Error moving project:', error);
        context?.previous.forEach(([key, value]) => {
          queryClient.setQueryData(key, value);
        });
      },
      onSettled: (_data, _error, { projectId }) => {
        queryClient.invalidateQueries({ queryKey: ['projects'] });
        queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      },
    });
};

/**
 * Show or hide a project on the listing map, for everyone. Optimistic like
 * pinning: the eye flips and the polygon goes at once.
 */
export const useSetProjectMapVisible = () => {
    const queryClient = useQueryClient();

    return useMutation<
      ApiResponse<Project>,
      ApiError,
      { projectId: string; mapVisible: boolean },
      { previous: [readonly unknown[], ApiResponse<Project[]>][] }
    >({
      mutationFn: ({ projectId, mapVisible }) => {
        return api.request<ApiResponse<Project>>(`/api/projects/${projectId}/`, {
          method: 'PATCH',
          body: { map_visible: mapVisible },
        });
      },
      onMutate: async ({ projectId, mapVisible }) => {
        await queryClient.cancelQueries({ queryKey: ['projects'] });
        const previous = queryClient.getQueriesData<ApiResponse<Project[]>>({
          queryKey: ['projects'],
        }) as [readonly unknown[], ApiResponse<Project[]>][];
        previous.forEach(([key, value]) => {
          if (!value?.data) return;
          queryClient.setQueryData(key, {
            ...value,
            data: value.data.map((p) =>
              String(p.id) === String(projectId) ? { ...p, map_visible: mapVisible } : p
            ),
          });
        });
        return { previous };
      },
      onError: (error, _variables, context) => {
        console.error('Error updating map visibility:', error);
        context?.previous.forEach(([key, value]) => {
          queryClient.setQueryData(key, value);
        });
      },
      onSettled: (_data, _error, { projectId }) => {
        queryClient.invalidateQueries({ queryKey: ['projects'] });
        queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      },
    });
};

/**
 * Set how a project's property boundaries are drawn (null = default).
 * Optimistic on every cached projects list so the listing map restyles as
 * the dialog's sliders move; the server validates and a refusal rolls back.
 */
export const useSetProjectPropertyStyle = () => {
    const queryClient = useQueryClient();

    return useMutation<
      ApiResponse<Project>,
      ApiError,
      { projectId: string; style: BoundaryStyle | null },
      { previous: [readonly unknown[], ApiResponse<Project[]>][] }
    >({
      mutationFn: ({ projectId, style }) => {
        return api.request<ApiResponse<Project>>(`/api/projects/${projectId}/`, {
          method: 'PATCH',
          body: { property_style: style },
        });
      },
      onMutate: async ({ projectId, style }) => {
        await queryClient.cancelQueries({ queryKey: ['projects'] });
        const previous = queryClient.getQueriesData<ApiResponse<Project[]>>({
          queryKey: ['projects'],
        }) as [readonly unknown[], ApiResponse<Project[]>][];
        previous.forEach(([key, value]) => {
          if (!value?.data) return;
          queryClient.setQueryData(key, {
            ...value,
            data: value.data.map((p) =>
              String(p.id) === String(projectId) ? { ...p, property_style: style } : p
            ),
          });
        });
        return { previous };
      },
      onError: (error, _variables, context) => {
        console.error('Error updating property boundary style:', error);
        context?.previous.forEach(([key, value]) => {
          queryClient.setQueryData(key, value);
        });
      },
      onSettled: (_data, _error, { projectId }) => {
        queryClient.invalidateQueries({ queryKey: ['projects'] });
        queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      },
    });
};
